import {
  attachSamplePack,
  createComplianceCase,
  generatePassport,
  validateComplianceCase,
} from "../api";
import type { ComplianceCaseDetail } from "../compliance/types";
import { TIMBER_DE_DEMO_PRESET } from "../compliance/ux/demoPreset";

export type InvestorDemoStepId = "create" | "documents" | "validate" | "passport";

export type InvestorDemoStep = {
  id: InvestorDemoStepId;
  status: "pending" | "running" | "done" | "error";
};

export type InvestorDemoResult = {
  caseDetail: ComplianceCaseDetail;
  steps: InvestorDemoStep[];
};

/** US-401: scripted create → sample pack → validate → passport. */
export async function runInvestorDemo(
  onStep?: (steps: InvestorDemoStep[]) => void
): Promise<InvestorDemoResult> {
  const steps: InvestorDemoStep[] = [
    { id: "create", status: "pending" },
    { id: "documents", status: "pending" },
    { id: "validate", status: "pending" },
    { id: "passport", status: "pending" },
  ];

  const emit = () => onStep?.(steps.map((s) => ({ ...s })));

  const mark = (id: InvestorDemoStepId, status: InvestorDemoStep["status"]) => {
    const step = steps.find((s) => s.id === id);
    if (step) step.status = status;
    emit();
  };

  mark("create", "running");
  let caseDetail: ComplianceCaseDetail;
  try {
    caseDetail = await createComplianceCase({
      ...TIMBER_DE_DEMO_PRESET,
      contract_reference: `INVESTOR-DEMO-${Date.now()}`,
    });
    mark("create", "done");
  } catch (err) {
    mark("create", "error");
    throw err;
  }

  mark("documents", "running");
  try {
    caseDetail = await attachSamplePack(caseDetail.id);
    mark("documents", "done");
  } catch (err) {
    mark("documents", "error");
    throw err;
  }

  mark("validate", "running");
  try {
    await validateComplianceCase(caseDetail.id);
    mark("validate", "done");
  } catch (err) {
    mark("validate", "error");
    throw err;
  }

  mark("passport", "running");
  try {
    caseDetail = await generatePassport(caseDetail.id);
    mark("passport", "done");
  } catch (err) {
    mark("passport", "error");
    throw err;
  }

  return { caseDetail, steps };
}
